import React from 'react';
import { Link } from 'react-router-dom';
import './PolicyPages.css';

const PrivacyPage = () => {
  return (
    <div className="policy-page">
      <div className="policy-container">
        <Link to="/" className="back-link">← Back to SlimBuddy</Link>
        
        <h1>SlimBuddy Privacy Policy</h1>
        
        <div className="effective-date">
          <strong>Last Updated:</strong> September 28, 2025
        </div>

        <h2>Introduction</h2>
        <p>SlimBuddy and QFL Capital LLC. ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our mobile fitness application and related services.</p>

        <h2>1. Information We Collect</h2>

        <h3>Personal Information You Provide</h3>
        <ul>
          <li>Account Information: Email address, full name, password</li>
          <li>Profile Information: Date of birth, gender, height, weight, activity level, fitness goals</li>
          <li>Unit Preferences: Imperial or metric measurement preferences</li>
        </ul>

        <h3>Health and Fitness Data</h3>
        <ul>
          <li>Manual Entries: Food consumption logs (food items, quantities, nutritional values), exercise entries (type, duration, calories burned), weight measurements</li>
          <li>Apple HealthKit Data (with your consent):</li>
          <ul>
            <li>Step count and walking/running distances</li>
            <li>Workout data (type, duration, active energy burned, heart rate)</li>
            <li>Active energy burned</li>
            <li>Exercise minutes and movement data</li>
          </ul>
        </ul>

        <h3>Usage and Technical Data</h3>
        <ul>
          <li>App Usage: Feature interactions, session duration, error reports</li>
          <li>Device Information: iOS version, device model, app version</li>
          <li>Location Data: None collected</li>
          <li>Chat/Communication Data: Messages and interactions with our AI-powered nutrition and fitness assistant</li>
        </ul>

        <h2>2. How We Use Your Information</h2>

        <h3>Core App Functionality</h3>
        <ul>
          <li>Provide personalized nutrition and fitness recommendations</li>
          <li>Calculate and track daily nutritional targets (calories, protein, carbs, fat)</li>
          <li>Monitor fitness progress and achievements</li>
          <li>Generate daily health and fitness summaries</li>
          <li>Sync and aggregate health data from multiple sources</li>
        </ul>

        <h3>AI-Powered Features</h3>
        <ul>
          <li>Food recognition from photos and text descriptions</li>
          <li>Exercise logging and calorie calculations</li>
          <li>Personalized coaching and recommendations</li>
          <li>Daily digest generation with insights</li>
        </ul>

        <h3>Account and Security</h3>
        <ul>
          <li>Create and manage your user account</li>
          <li>Authenticate your identity and secure your data</li>
          <li>Provide customer support and respond to inquiries</li>
          <li>Send important service updates and security notifications</li>
        </ul>

        <h2>3. Information Sharing and Disclosure</h2>

        <h3>Third-Party Service Providers</h3>
        <p>We share your data with trusted service providers who assist us in operating the app:</p>
        <ul>
          <li>Supabase: Secure cloud database hosting for account and health data storage</li>
          <li>N8N Automation Platform: Processing workflows for AI features and data analysis. We host N8N on our servers and do not send information to N8N Cloud.</li>
          <li>Apple HealthKit: Health data integration (data remains on your device unless explicitly shared)</li>
        </ul>

        <h3>Data Processing</h3>
        <ul>
          <li>All data processing occurs through secure, encrypted webhooks</li>
          <li>Your health data is processed to provide personalized recommendations</li>
          <li>We never sell your personal information to advertisers or marketers</li>
        </ul>

        <h3>Legal Requirements</h3>
        <p>We may disclose your information if required by law, regulation, or legal process, or to protect the rights, property, or safety of SlimBuddy, our users, or others.</p>

        <h2>4. Data Storage and Security</h2>

        <h3>Security Measures</h3>
        <ul>
          <li>End-to-end encryption for data transmission</li>
          <li>Secure cloud storage with enterprise-grade security (Supabase)</li>
          <li>Regular security audits and monitoring</li>
          <li>Multi-factor authentication for account protection</li>
        </ul>

        <h3>Data Retention</h3>
        <ul>
          <li>Account data: Retained while your account is active</li>
          <li>Health data: Retained to provide continuous service and track progress</li>
          <li>You may request data deletion at any time</li>
        </ul>

        <h2>5. Your Rights and Choices</h2>

        <h3>Access and Control</h3>
        <ul>
          <li>View Your Data: Access all personal information we've collected</li>
          <li>Update Information: Modify your profile and preferences at any time</li>
          <li>Delete Data: Request deletion of your account and associated data</li>
          <li>Export Data: Download your health and fitness data</li>
        </ul>

        <h3>HealthKit Controls</h3>
        <ul>
          <li>Enable/disable HealthKit integration at any time through iOS Settings</li>
          <li>Choose which health data types to share with SlimBuddy</li>
          <li>Revoke permissions through your device's Privacy settings</li>
        </ul>

        <h3>Communication Preferences</h3>
        <ul>
          <li>Opt out of non-essential notifications</li>
          <li>Control in-app messaging preferences</li>
        </ul>

        <h2>6. Children's Privacy</h2>
        <p>SlimBuddy is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us immediately.</p>

        <h2>7. International Data Transfers</h2>
        <p>Your information may be processed and stored in the United States and other countries where our service providers operate. We ensure appropriate safeguards are in place for international transfers.</p>

        <h2>8. Changes to This Policy</h2>
        <p>We may update this Privacy Policy periodically. We will notify users of significant changes through the app or via email. Continued use of SlimBuddy after changes constitutes acceptance of the updated policy.</p>

        <h2>9. Contact Information</h2>
        <p>For privacy-related questions or requests:<br />
        Email: <a href="mailto:info@slimbuddy.ai">info@slimbuddy.ai</a></p>

        <h2>10. Legal Basis for Processing (GDPR)</h2>
        <p>For EU users, we process your data based on:</p>
        <ul>
          <li>Consent: For HealthKit data and optional features</li>
          <li>Contract Performance: To provide app services you've requested</li>
          <li>Legitimate Interests: To improve our services and ensure security</li>
          <li>Legal Obligations: To comply with applicable laws</li>
        </ul>
      </div>
    </div>
  );
};

export default PrivacyPage;